import { Box, HStack, Skeleton, Table, Text } from '@chakra-ui/react'

import { useRefreshCountdown } from '../../hooks/useRefreshCountdown'
import { useHotelsI18n } from '../../i18n'
import type { EmergencyCall } from '../../types/callHistory'
import { formatCallTime, formatDuration } from '../../utils/formatting'

interface EmergencyCallsTableProps {
  calls: EmergencyCall[]
  isLoading?: boolean
}

/**
 * 911/933 emergency call history table matching legacy layout.
 * Columns: Room | Time & Date | Duration
 * Contained in a bordered box with sticky header and scrollable body.
 */
export const EmergencyCallsTable = ({
  calls,
  isLoading = false,
}: EmergencyCallsTableProps) => {
  const SKELETON_ROWS = 6
  const { text } = useHotelsI18n()
  const safeCalls = Array.isArray(calls) ? calls : []
  const secondsLeft = useRefreshCountdown(60)

  return (
    <Box
      borderWidth={1}
      borderColor="border.default"
      borderRadius="lg"
      bg="bg.default"
      overflow="hidden"
    >
      <Box overflowX="auto">
        <Table.Root size="sm" variant="line">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader px={4} py={2}>
                {text('hotels.dashboard.room', 'Room')}
              </Table.ColumnHeader>
              <Table.ColumnHeader px={4} py={2}>
                {text('hotels.dashboard.timeAndDate', 'Time & Date')}
              </Table.ColumnHeader>
              <Table.ColumnHeader px={4} py={2}>
                {text('hotels.dashboard.duration', 'Duration')}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                <Table.Row key={`emergency-skeleton-${index}`}>
                  <Table.Cell px={4} py={2}>
                    <Skeleton h="14px" w="52px" />
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Skeleton h="14px" w="150px" />
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Skeleton h="14px" w="70px" />
                  </Table.Cell>
                </Table.Row>
              ))
            ) : safeCalls.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={3} textAlign="center" py={8}>
                  <Text color="fg.muted">
                    {text('hotels.dashboard.noEmergencyCalls', 'No Calls')}
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              safeCalls.map((call) => (
                <Table.Row key={call.id}>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">{call.src_ext}</Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">{formatCallTime(call.call_time)}</Text>
                  </Table.Cell>
                  <Table.Cell px={4} py={2}>
                    <Text fontSize="sm">
                      {formatDuration(call.billable_duration)}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Footer bar */}
      <HStack
        justify="space-between"
        px={4}
        py={2}
        borderTopWidth={1}
        borderColor="border.default"
        bg="bg.subtle"
      >
        <Text fontSize="xs" color="fg.muted">
          {isLoading
            ? text(
                'hotels.dashboard.loadingEmergencyCalls',
                'Loading emergency calls...',
              )
            : text(
                'hotels.dashboard.emergencyFooter',
                '* Call placed to {{destination}}',
                {
                  destination: safeCalls[0]?.destination_number ?? '911/933',
                },
              )}
        </Text>
        <Text fontSize="xs" color="fg.subtle">
          {text('hotels.dashboard.refreshingIn', 'Refreshing in {{seconds}}s', {
            seconds: secondsLeft,
          })}
        </Text>
      </HStack>
    </Box>
  )
}
